import {
  Radio,
  MoreVertical,
  Pencil,
  Trash2,
  FileSpreadsheet,
  Sparkles,
  Zap,
  Mail,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import type { StreamForIndex } from '@/types/streams'

interface StreamCardProps {
  stream: StreamForIndex
  onEdit?: () => void
  onDelete?: () => void
  onToggleStatus?: () => void
  onViewReports?: () => void
  onUploadCsv?: () => void
  onViewGems?: () => void
}

export function StreamCard({
  stream,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewReports,
  onUploadCsv,
  onViewGems,
}: StreamCardProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen])

  const isActive = stream.status === 'active'

  // Format item count
  const formatCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`
    }
    return count.toString()
  }

  // Format short date with time for schedule icons
  const formatShortDate = (dateStr: string | null) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    const hour12 = hours % 12 || 12
    const timePart = minutes > 0 ? `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}` : `${hour12}${ampm}`
    return `${datePart} · ${timePart}`
  }

  const nextScheduledReport = formatShortDate(stream.nextScheduledReportAt)
  const nextDelivery = formatShortDate(stream.nextDeliveryAt)

  return (
    <div
      onClick={onEdit}
      className="group w-full text-left rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-4 hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-sm transition-all cursor-pointer"
    >
      {/* Top Row: Icon, Title, Actions */}
      <div className="flex items-center gap-3">
        {/* Stream Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
            isActive
              ? 'bg-teal-100 dark:bg-teal-900/40 group-hover:bg-teal-200 dark:group-hover:bg-teal-900/60'
              : 'bg-stone-100 dark:bg-stone-800'
          }`}
        >
          <Radio
            className={`w-5 h-5 ${
              isActive
                ? 'text-teal-600 dark:text-teal-400'
                : 'text-stone-400 dark:text-stone-500'
            }`}
          />
        </div>

        {/* Title and Stats */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-stone-900 dark:text-stone-100 truncate group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
            {stream.name}
          </h3>
          {/* Stats inline with title */}
          <div className="flex items-center gap-2 mt-1 text-xs text-stone-400 dark:text-stone-500">
            <span className="flex items-center gap-1.5">
              <span className="font-medium text-stone-500 dark:text-stone-400">{formatCount(stream.unprocessedItemsCount)}</span> items pending
              {/* Mini progress bar - only show if progress > 0 */}
              {stream.thresholdPercentage > 0 && (
                <div className="w-12 h-1.5 bg-stone-200 dark:bg-stone-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-600 dark:bg-teal-500 transition-all duration-300"
                    style={{ width: `${stream.thresholdPercentage}%` }}
                  />
                </div>
              )}
            </span>
            <span className="text-stone-300 dark:text-stone-600">·</span>
            <span>
              <span className="font-medium text-stone-500 dark:text-stone-400">{stream.reportCount}</span> {stream.reportCount === 1 ? 'report' : 'reports'}
            </span>
          </div>
        </div>

        {/* Schedule Badge */}
        {(nextScheduledReport || nextDelivery) && (
          <div
            className="hidden sm:inline-flex items-center gap-2.5 px-2.5 py-1 rounded-lg bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 flex-shrink-0"
          >
            {nextScheduledReport && (
              <span className="flex items-center gap-1" title="Next scheduled report generation">
                <Zap className="w-3 h-3 text-amber-500" />
                <span className="text-xs font-medium text-stone-600 dark:text-stone-400">{nextScheduledReport}</span>
              </span>
            )}
            {nextDelivery && (
              <span className="flex items-center gap-1" title="Next report delivery">
                <Mail className="w-3 h-3 text-teal-500" />
                <span className="text-xs font-medium text-stone-600 dark:text-stone-400">{nextDelivery}</span>
              </span>
            )}
          </div>
        )}

        {/* Toggle Switch */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onToggleStatus?.()
          }}
          role="switch"
          aria-checked={isActive}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-stone-900 flex-shrink-0 ${
            isActive
              ? 'bg-emerald-500'
              : 'bg-stone-200 dark:bg-stone-700'
          }`}
          title={isActive ? 'Pause stream' : 'Activate stream'}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
              isActive ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>

        {/* Overflow Menu */}
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen(!menuOpen)
            }}
            className="p-2 -mr-2 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 dark:hover:text-stone-300 dark:hover:bg-stone-800 transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-20">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onEdit?.()
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onUploadCsv?.()
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Upload CSV
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onViewGems?.()
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Gems
              </button>

              <div className="border-t border-stone-200 dark:border-stone-700 my-1" />

              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onDelete?.()
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
